/** Target horizontal resolution: one plotted point per this many CSS pixels (2–4 px range). */
export const PIXELS_PER_POINT = 3;
/** Floor so very narrow charts do not over-merge. */
export const MIN_RENDERABLE_POINTS = 48;
function pad2(n) {
    return n.toString().padStart(2, "0");
}
export function formatChartAxisLabel(iso, compact) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    const d = `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}`;
    const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    if (compact)
        return `${d}\n${time}`;
    return `${d} ${time}`;
}
/**
 * Builds chart points: raw (one per log) when count fits the chart width; otherwise
 * equal-width time buckets with average (line) and min/max for tooltips only.
 * FatDuration is carried through on both paths; buckets average non-null fat values.
 */
export function buildTemperatureChartSeries(logs, chartWidthPx, compactLabels) {
    if (!logs.length || chartWidthPx < 1)
        return [];
    const sorted = [...logs]
        .filter((l) => l.dateTime)
        .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
    const withTemp = sorted.filter((l) => l.temperature != null && !Number.isNaN(Number(l.temperature)));
    if (withTemp.length === 0)
        return [];
    const maxRenderable = Math.max(MIN_RENDERABLE_POINTS, Math.floor(chartWidthPx / PIXELS_PER_POINT));
    if (withTemp.length <= maxRenderable) {
        return withTemp.map((l) => {
            var _a;
            return ({
                month: formatChartAxisLabel(l.dateTime, compactLabels),
                dateTimeIso: l.dateTime,
                temperature: Number(l.temperature),
                fatDuration: (_a = l.fatDuration) !== null && _a !== void 0 ? _a : null,
                isAggregated: false,
                alarms: [],
            });
        });
    }
    const t0 = new Date(withTemp[0].dateTime).getTime();
    const t1 = new Date(withTemp[withTemp.length - 1].dateTime).getTime();
    const span = Math.max(1, t1 - t0);
    const bucketMs = span / maxRenderable;
    const out = [];
    for (let i = 0; i < maxRenderable; i++) {
        const start = t0 + i * bucketMs;
        const end = i === maxRenderable - 1 ? t1 + 1 : t0 + (i + 1) * bucketMs;
        const pts = withTemp.filter((p) => {
            const t = new Date(p.dateTime).getTime();
            return t >= start && t < end;
        });
        if (pts.length === 0)
            continue;
        const nums = pts.map((p) => Number(p.temperature));
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        const minT = Math.min(...nums);
        const maxT = Math.max(...nums);
        const labelIso = new Date(start).toISOString();
        const fatValues = pts
            .map((p) => p.fatDuration)
            .filter((v) => v != null);
        const avgFat = fatValues.length > 0
            ? Math.round(fatValues.reduce((a, b) => a + b, 0) / fatValues.length)
            : null;
        out.push({
            month: formatChartAxisLabel(labelIso, compactLabels),
            dateTimeIso: labelIso,
            temperature: Math.round(avg * 10) / 10,
            minTemperature: minT,
            maxTemperature: maxT,
            fatDuration: avgFat,
            isAggregated: true,
            alarms: [],
        });
    }
    return out;
}
